import { useCallback, useEffect, useRef, useState } from 'react';
import { CompanyRequestError } from '@/company/api';
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';

export interface Remote<T> {
  data: T | null;
  loading: boolean;
  error: string;
  refresh: () => void;
}

/**
 * Loads `load()` whenever `key` changes and keeps the last answer on screen while it reloads.
 * While `poll` is true the next request starts only after the previous one has finished, and
 * waits longer after each failure (up to ten times `pollMs`), so a failing poll neither stacks
 * requests nor flips the page between loading and error.
 */
export function useRemote<T>(
  load: (() => Promise<T>) | null,
  key: string,
  poll = false,
  pollMs = 3000,
): Remote<T> {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string }>({
    data: null,
    loading: Boolean(load),
    error: '',
  });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  // The request whose result (answer or error) is on screen. Switching polling off
  // does not retry a settled request; a run cut off before settling still asks.
  const answered = useRef<{ key: string; tick: number } | null>(null);
  useEffect(() => {
    if (!poll && answered.current?.key === key && answered.current?.tick === tick) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const run = async (first: boolean) => {
      const current = loadRef.current;
      if (!current) {
        setState({ data: null, loading: false, error: '' });
        return;
      }
      if (first) setState((previous) => ({ ...previous, loading: true, error: '' }));
      try {
        const data = await current();
        if (!alive) return;
        failures = 0;
        answered.current = { key, tick };
        setState({ data, loading: false, error: '' });
      } catch (failure) {
        if (!alive) return;
        failures += 1;
        answered.current = { key, tick };
        setState((previous) => ({ ...previous, loading: false, error: errorText(failure) }));
      }
      if (alive && poll)
        timer = setTimeout(() => void run(false), pollMs * Math.min(2 ** failures, 10));
    };
    // A new load replaces what was on screen: going back to an earlier key asks for it again, even
    // when that key answered before (another key may have left its error or a load in between).
    answered.current = null;
    void run(true);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [key, tick, poll, pollMs]);
  const refresh = useCallback(() => setTick((value) => value + 1), []);
  return { ...state, refresh };
}

/** The words for a refused Storefront request, picked by the server's error code. */
export function failureText(
  failure: unknown,
  copy: Record<StorefrontCopyKey, string>,
  fallback: string,
): string {
  const code = failure instanceof CompanyRequestError ? failure.code : '';
  // Out of credits (the company's, or the person's monthly limit): never the address or the shop.
  if (
    code === 'payment_required' ||
    (failure instanceof CompanyRequestError && failure.status === 402)
  )
    return copy.errorNoCredits;
  if (code === 'publish_permission_required') return copy.errorPublishPermission;
  if (code === 'company_assignment_required') return copy.errorNoCompany;
  if (code === 'invalid_store_url') return copy.urlInvalid;
  if (code === 'newer_version') return copy.errorNewerVersion;
  if (code === 'design_checks_failed') return copy.errorDesignChecks;
  if (code === 'design_samples_unavailable') return copy.designSamplesBlocked;
  if (code === 'store_read_unavailable') return copy.errorReadUnavailable;
  if (code === 'install_check_busy') return copy.installCheckBusy;
  return fallback;
}

export function errorText(failure: unknown): string {
  if (failure instanceof CompanyRequestError) return failure.message;
  return failure instanceof Error ? failure.message : 'request_failed';
}
